import {useEffect, useState} from 'react';

import {FolderSummary, FolderWithPermissions, MinimalFolder} from 'constants/FolderConstants';
import FolderDAO from 'file-browser/FolderDAO';
import notifications from 'notifications';

export type FolderScopeAccessStatus = 'accessible' | 'error' | 'inaccessible' | 'loading';

export interface FolderScopeAccessResult {
  folders?: Array<MinimalFolder>;
  status: FolderScopeAccessStatus;
}

interface FolderScopeAccessCheck extends FolderScopeAccessResult {
  folderIdsKey: string;
}

function toMinimalFolder(folder: FolderSummary): MinimalFolder {
  return {
    api_identifier: folder.api_identifier,
    archiveRecord: folder.archiveRecord,
    id: folder.api_identifier,
    name: folder.name,
    owner_handle: folder.owner_handle,
    parent: folder.parent == null ? null : toMinimalFolder(folder.parent),
    source: folder.source,
    type: folder.type,
  };
}

export default function useFolderScopeAccess(savedFolderIds: Array<string>): FolderScopeAccessResult {
  const savedFolderIdsKey = JSON.stringify(savedFolderIds);
  const [accessCheck, setAccessCheck] = useState<FolderScopeAccessCheck>({
    folderIdsKey: savedFolderIdsKey,
    status: savedFolderIds.length === 0 ? 'accessible' : 'loading',
  });
  const accessResult: FolderScopeAccessResult =
    accessCheck.folderIdsKey === savedFolderIdsKey
      ? accessCheck
      : {status: savedFolderIds.length === 0 ? 'accessible' : 'loading'};

  useEffect(() => {
    const folderIds = JSON.parse(savedFolderIdsKey) as Array<string>;
    if (folderIds.length === 0) {
      setAccessCheck({folderIdsKey: savedFolderIdsKey, status: 'accessible'});
      return;
    }

    let isMounted = true;
    FolderDAO.fetchAll({level: 'read'})
      .done((folders) => {
        if (!isMounted) {
          return;
        }
        const availableFolders = folders ?? [];
        const availableFolderIds = new Set(availableFolders.map((folder) => folder.api_identifier));
        const missingFolderIds = folderIds.filter((folderId) => !availableFolderIds.has(folderId));
        if (missingFolderIds.length === 0) {
          setAccessCheck({
            folderIdsKey: savedFolderIdsKey,
            folders: availableFolders,
            status: 'accessible',
          });
          return;
        }

        FolderDAO.bulkFetch(missingFolderIds)
          .then((foldersById) => {
            if (!isMounted) {
              return;
            }
            const readableMissingFolders = missingFolderIds
              .map((folderId) => foldersById[folderId])
              .filter((folder): folder is FolderWithPermissions => folder != null);
            setAccessCheck({
              folderIdsKey: savedFolderIdsKey,
              folders:
                readableMissingFolders.length === missingFolderIds.length
                  ? [...availableFolders, ...readableMissingFolders.map((folder) => toMinimalFolder(folder))]
                  : undefined,
              status:
                readableMissingFolders.length === missingFolderIds.length ? 'accessible' : 'inaccessible',
            });
          })
          .catch((error) => {
            if (isMounted) {
              notifications.notifyError(error);
              setAccessCheck({folderIdsKey: savedFolderIdsKey, status: 'error'});
            }
          });
      })
      .fail((error) => {
        if (isMounted) {
          notifications.notifyError(error);
          setAccessCheck({folderIdsKey: savedFolderIdsKey, status: 'error'});
        }
      });
    return () => {
      isMounted = false;
    };
  }, [savedFolderIdsKey]);

  return accessResult;
}
